import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  Bot,
  Globe,
  Layers,
  Link2,
  Mail,
  MapPin,
  Network,
  PenLine,
  RefreshCw,
  Star,
  VenetianMask,
} from "lucide-react";

export type Hat = "white" | "grey" | "black";
export type Risk = "none" | "medium" | "high" | "severe";

export interface HatCard {
  id: string;
  tactic: string;
  hat: Hat;
  risk: Risk;
  why: string;
  icon: LucideIcon;
  /** Marks the "wait, that's black hat now?!" twist cards. */
  twist?: boolean;
}

/** Deliberately interleaved so the room can't pattern-match a column. */
export const HAT_CARDS: HatCard[] = [
  {
    id: "outreach",
    tactic: "Polite outreach with research, data, or tools people want",
    hat: "white",
    risk: "none",
    why: "Earning links with real assets is the durable way.",
    icon: Mail,
  },
  {
    id: "paid-networks",
    tactic: "Buying links from paid networks / PBNs",
    hat: "black",
    risk: "severe",
    why: "Paid link networks are high risk: penalties, or links that are simply ignored.",
    icon: Link2,
  },
  {
    id: "guest-posts",
    tactic: "Guest posting mainly to get a link",
    hat: "grey",
    risk: "medium",
    why: "A great article on a relevant site is fine; mass-produced keyword-link posts are link spam.",
    icon: PenLine,
  },
  {
    id: "cloaking",
    tactic: "Cloaking: showing crawlers a different page than users",
    hat: "black",
    risk: "severe",
    why: "Deliberate manipulation; deindexing territory.",
    icon: VenetianMask,
  },
  {
    id: "internal-links",
    tactic: "Internal-linking cleanup for context and crawl paths",
    hat: "white",
    risk: "none",
    why: "Pure housekeeping: users, crawlers, and important pages all win.",
    icon: Network,
  },
  {
    id: "link-swaps",
    tactic: "“You link to me, I'll link to you” swap deals",
    hat: "grey",
    risk: "medium",
    why: "A few natural partner links are normal; systematic exchanges backfire when policies change.",
    icon: ArrowLeftRight,
  },
  {
    id: "comment-spam",
    tactic: "Automated comment & forum spam",
    hat: "black",
    risk: "severe",
    why: "Scaled spam is exactly what spam systems are built to catch.",
    icon: Bot,
  },
  {
    id: "expired-domains",
    tactic: "Expired-domain authority tricks",
    hat: "grey",
    risk: "high",
    why: "The classic example of grey turning black: policy changes made it a named spam pattern.",
    icon: Globe,
  },
  {
    id: "refresh-pages",
    tactic: "Refreshing key pages when facts change",
    hat: "white",
    risk: "none",
    why: "Updated, reliable content is the through-line of everything Google rewards.",
    icon: RefreshCw,
  },
  {
    id: "fake-reviews",
    tactic: "Buying fake reviews",
    hat: "black",
    risk: "severe",
    why: "Fake reviews hurt trust everywhere — search, platforms, and often the law.",
    icon: Star,
  },
  {
    id: "scaled-content",
    tactic: "Scaled low-value content with no human review",
    hat: "black",
    risk: "high",
    why: "Thin, scaled content is a named target of search quality and spam systems.",
    icon: Layers,
    // twist: true,
  },
  {
    id: "doorway-pages",
    tactic: "Doorway pages with swapped city names",
    hat: "black",
    risk: "high",
    why: "Local landing pages need real local content — city-swap doorways are a named spam pattern.",
    icon: MapPin,
    // twist: true,
  },
];
